import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Toolbar,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import apiAxiosInstance from '../../utils/axiosInstance';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import html2pdf from 'html2pdf.js';
import { GraphRenderer } from '../Common/GraphRenderer';
import { RectangularPrism } from '../Common/RectangularPrism';
import { Cube } from '../Common/Cube';
import { Sphere } from '../Common/Sphere';
import { Cylinder } from '../Common/Cylinder';
import { Cone } from '../Common/Cone';
import { Pyramid } from '../Common/Pyramid';
import { TriangularPrism } from '../Common/TriangularPrism';
import { Hemisphere } from '../Common/Hemisphere';
import { Square } from '../Common/Square';
import { Rectangle } from '../Common/Rectangle';
import { Triangle } from '../Common/Triangle';
import { Cube2D } from '../Common/Cube2D';
import CoordinatePlane from '../Common/CoordinatePlane';
import { GraphData } from '../../Types/graph';

// Interface definitions
interface AnswerObject {
  text: string;
}

interface Question {
  question: string;
  answers: Array<string | AnswerObject>;
  correctAnswer: string;
  explanation?: string;
  standardReference?: string;
  bloomsLevel?: string;
  answerType?: string;
  graph?: GraphData;
  visualizationType?: string;
  is3D?: boolean;
  useCustomRenderer?: boolean;
  dimensions?: Record<string, number>;
}

interface MathWordProblemListItem {
  _id: string;
  title: string;
  gradeLevel: string;
  numQuestions: number;
  language: string;
  questionStyle: string;
  createdAt: string;
  updatedAt: string;
}

interface MathWordProblem {
  _id: string;
  title: string;
  gradeLevel: string;
  strand?: string;
  questions: Question[];
  answerKey?: string;
  standards?: Array<{
    code: string;
    description: string;
  }>;
  numQuestions: number;
  language: string;
  questionStyle: string;
  questionMix: string;
  createdAt: string;
  updatedAt: string;
}

interface ViewDialogProps {
  open: boolean;
  problemSet: MathWordProblem | null;
  onClose: () => void;
}

function isAnswerObject(answer: string | AnswerObject): answer is AnswerObject {
  return typeof answer === 'object' && answer !== null && 'text' in answer;
}

// Component for displaying individual questions in the view dialog
const QuestionDisplay: React.FC<{ 
  question: Question; 
  index: number; 
  showAnswerKey: boolean; 
}> = ({ question, index, showAnswerKey }) => {
  const renderVisualization = () => {
    if (!question.graph || !question.graph.type) return null;

    const vizType = question.visualizationType;
    const dims = question.dimensions || (question.graph?.config as any)?.shape3DConfig?.dimensions;
    const unit = (question.graph?.config as any)?.shape3DConfig?.unit || 'cm';
    const color = (question.graph?.config as any)?.shape3DConfig?.color;

    switch (vizType) {
      case 'rectangular_prism':
        return (
          <RectangularPrism
            length={dims?.length || 10}
            width={dims?.width || 6}
            height={dims?.height || 4}
            unit={unit}
            color={color}
          />
        );
      case 'cube':
        return <Cube side={dims?.side || 4} unit={unit} color={color} />;
      case 'sphere':
        return <Sphere radius={dims?.radius || 3} unit={unit} color={color} />;
      case 'cylinder':
        return <Cylinder radius={dims?.radius || 2} height={dims?.height || 5} unit={unit} color={color} />;
      case 'cone':
        return (
          <Cone
            radius={dims?.radius ?? 3}
            height={dims?.height}
            slantHeight={dims?.slantHeight}
            unit={unit}
            color={color}
          />
        );
      case 'pyramid':
        return (
          <Pyramid
            base={dims?.base ?? 4}
            height={dims?.height}
            slantHeight={dims?.slantHeight}
            unit={unit}
            color={color}
          />
        );
      case 'triangular_prism':
        return (
          <TriangularPrism
            base={dims?.base || 4}
            height={dims?.height || 3}
            length={dims?.length || 6}
            unit={unit}
            color={color}
          />
        );
      case 'hemisphere':
        return <Hemisphere radius={dims?.radius || 3} unit={unit} color={color} />;
      case 'square':
        return <Square side={dims?.side || 4} unit={unit} color={color} strokeColor={color} />;
      case 'rectangle':
        return (
          <Rectangle
            length={dims?.length || 8}
            width={dims?.width || 5}
            unit={unit}
            color={color}
            strokeColor={color}
          />
        );
      case 'triangle':
        const triangleConfig = (question.graph?.config as any)?.shape3DConfig || {};
        return (
          <Triangle
            base={dims?.base || 6}
            height={dims?.height || 5}
            side={dims?.side}
            hypotenuse={dims?.hypotenuse}
            unit={unit}
            triangleType={triangleConfig.triangleType || 'scalene'}
            color={color}
            strokeColor={color}
            withAngles={triangleConfig.withAngles || false}
            angles={triangleConfig.angles}
            showHypotenuse={triangleConfig.showHypotenuse || false}
            showSideLabels={triangleConfig.showSideLabels !== false}
          />
        );
      case 'cube2d':
        return <Cube2D side={dims?.side || 4} unit={unit} color={color} strokeColor={color} />;
      case 'coordinate_plane': {
        const graphConfig = question.graph?.config || {};
        const graphData = question.graph?.data || [];
        const coordConfig = graphConfig as any;
        
        const points = graphData.map((item: any, idx: number) => {
          const x = typeof item.x === 'number' ? item.x : Number(item.x ?? 0);
          const y = typeof item.y === 'number' ? item.y : Number(item.y ?? 0);
          return {
            label: item.label || item.name || String.fromCharCode(65 + idx),
            x: Number.isFinite(x) ? x : 0,
            y: Number.isFinite(y) ? y : 0,
            color: item.color,
          };
        });
        
        let gridSize = {
          minX: 0,
          maxX: 10,
          minY: 0,
          maxY: 10,
        };
        
        if (graphConfig.xDomain && graphConfig.yDomain) {
          gridSize = {
            minX: graphConfig.xDomain[0],
            maxX: graphConfig.xDomain[1],
            minY: graphConfig.yDomain[0],
            maxY: graphConfig.yDomain[1],
          };
        } else if (points.length > 0) {
          const pad = 1;
          const xValues = points.map(p => p.x);
          const yValues = points.map(p => p.y);
          gridSize = {
            minX: Math.min(0, ...xValues) - pad,
            maxX: Math.max(0, ...xValues) + pad,
            minY: Math.min(0, ...yValues) - pad,
            maxY: Math.max(0, ...yValues) + pad,
          };
        }
        
        const lines: Array<{ from: string; to: string; color?: string; style?: 'solid' | 'dashed' }> = [];
        if (coordConfig.lines && Array.isArray(coordConfig.lines)) {
          lines.push(...coordConfig.lines);
        }
        
        const coordinatePlaneConfig = {
          gridSize,
          points,
          lines: lines.length > 0 ? lines : undefined,
          showGrid: graphConfig.showGrid !== false,
          showAxisLabels: coordConfig.showAxisLabels !== false,
          showNumberLabels: coordConfig.showNumberLabels !== false,
          showCoordinateLines: coordConfig.showCoordinateLines !== false,
          title: graphConfig.title,
          description: coordConfig.description,
        };
        
        return <CoordinatePlane config={coordinatePlaneConfig} />;
      }
      default:
        return <GraphRenderer graphData={question.graph} />;
    }
  };

  return (
    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
      <Box sx={{ mb: 1, '& .katex': { fontSize: '1.1em' } }}>
        <Typography variant="body1" component="span" sx={{ fontWeight: 'bold' }}>
          {index + 1}.{' '}
        </Typography>
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
          {question.question}
        </ReactMarkdown>
      </Box>
      
      {question.graph && question.graph.type && (
        <Box sx={{ 
          my: 2,
          maxWidth: '600px',
          maxHeight: '500px',
          width: '100%',
          mx: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          '& svg, & canvas': {
            maxWidth: '100%',
            maxHeight: '500px',
            width: 'auto',
            height: 'auto'
          }
        }}>
          {renderVisualization()}
        </Box>
      )}
      
      {question.answers.map((answer, answerIndex) => {
        const answerText = isAnswerObject(answer) ? answer.text : String(answer);
        const cleanAnswer = answerText.replace(/^[A-Da-d][\.\)\:\s]+\s*/, '');
        return (
          <Box key={answerIndex} sx={{ ml: 2, mb: 0.5, display: 'flex', alignItems: 'flex-start', '& .katex': { fontSize: '1em' } }}>
            <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2', minWidth: '28px', mr: 1 }}>
              {String.fromCharCode(65 + answerIndex)}.
            </Typography>
            <Box sx={{ flex: 1, '& p': { m: 0 } }}>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                {cleanAnswer}
              </ReactMarkdown>
            </Box>
          </Box>
        );
      })}
      {showAnswerKey && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.light', borderRadius: 0.5 }}>
          <Typography variant="body2" color="success.dark">
            <strong>Correct Answer:</strong> {question.correctAnswer}
          </Typography>
          {question.explanation && (
            <Typography variant="body2" color="success.dark">
              <strong>Explanation:</strong> {question.explanation}
            </Typography>
          )}
          {question.standardReference && (
            <Typography variant="body2" color="success.dark">
              <strong>Standard:</strong> {question.standardReference}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

// Dialog component for viewing problem set details
const ViewProblemSetDialog: React.FC<ViewDialogProps> = ({ open, problemSet, onClose }) => {
  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState<null | HTMLElement>(null);

  if (!problemSet) return null;

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const createPrintContent = (
    current: MathWordProblem,
    format: 'print' | 'pdf' | 'word' = 'print'
  ) => {
    let content = '';
    
    // Title
    content += `<h1 style="text-align: center; color: #9c27b0; font-weight: 700; margin-bottom: 1.5em;">${current.title}</h1>`;
    
    // Questions
    current.questions.forEach((q, index) => {
      content += `<div class="question" style="margin-bottom: 2em; page-break-inside: avoid;">`;
      content += `<h2 style="color: #9c27b0; font-weight: 700; margin-bottom: 0.5em; border-bottom: 1px solid #e0e0e0; padding-bottom: 0.5em;">Question ${index + 1}</h2>`;
      content += `<div class="question-text" style="margin-bottom: 1em; line-height: 1.8;">${q.question}</div>`;
      
      if (q.graph && q.graph.type) {
        content += `<div class="graph-placeholder" style="text-align: center; margin: 1em 0; font-style: italic; color: #666;">[Graph visualization]</div>`;
      }
      
      if (q.answers && q.answers.length > 0) {
        content += `<div class="answer-choices" style="margin-left: 1em; margin-top: 0.5em;">`;
        q.answers.forEach((answer, j) => {
          let cleanAnswer = isAnswerObject(answer) ? answer.text : String(answer);
          cleanAnswer = cleanAnswer.replace(/^[A-Da-d][\.\)\:\s]+\s*/, '');
          const letter = String.fromCharCode(65 + j);
          content += `
            <div class="answer-choice" style="display: flex; margin-bottom: 0.5em; align-items: flex-start; page-break-inside: avoid;">
              <span class="answer-letter" style="font-weight: bold; margin-right: 0.5em; min-width: 1.5em; color: #7b1fa2;">${letter}.</span>
              <span class="answer-text" style="flex: 1;">${cleanAnswer}</span>
            </div>
          `;
        });
        content += `</div>`;
      }
      
      content += `</div>`;
    });
    
    // Answer Key
    if (current.answerKey) {
      content += `<div class="answer-key-section" style="page-break-before: always; margin-top: 2em; padding-top: 1em; border-top: 2px solid #9c27b0;">`;
      content += `<h2 style="text-align: center; color: #9c27b0; font-weight: 700; margin-bottom: 1em;">Comprehensive Answer Key</h2>`;
      const answerKeyHtml = current.answerKey
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>');
      content += `<div style="line-height: 1.8;">${answerKeyHtml}</div>`;
      content += `</div>`;
    } else if (current.questions.length > 0) {
      content += `<div class="answer-key-section" style="page-break-before: always; margin-top: 2em; padding-top: 1em; border-top: 2px solid #9c27b0;">`;
      content += `<h2 style="color: #9c27b0; font-weight: 700; margin-bottom: 1em;">Answer Key</h2>`;
      content += `<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.5em;">`;
      current.questions.forEach((q, idx) => {
        content += `<div>${idx + 1}. ${q.correctAnswer}</div>`;
      });
      content += `</div></div>`;
    }
    
    return content;
  };

  const handlePrint = () => {
    if (!problemSet) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = createPrintContent(problemSet, 'print');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${problemSet.title}</title>
          <style>
            body { font-family: 'Roboto', Arial, sans-serif; font-size: 14px; line-height: 1.6; padding: 40px; }
            h1 { text-align: center; color: #9c27b0; font-weight: 700; margin-bottom: 1em; }
            h2 { color: #9c27b0; font-weight: 700; border-bottom: 1px solid #e0e0e0; padding-bottom: 0.5em; }
            .question { page-break-inside: avoid; }
            .answer-choice { page-break-inside: avoid; }
            @media print {
              @page { margin: 0.5in; }
              body { padding: 0; }
              .answer-key-section { page-break-before: always; }
            }
          </style>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/katex.min.css">
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  const handleDownloadPDF = async () => {
    if (!problemSet) return;
    const content = createPrintContent(problemSet, 'pdf');
    const element = document.createElement('div');
    element.innerHTML = `
      <style>
        body { font-family: 'Roboto', Arial, sans-serif; font-size: 14px; line-height: 1.6; }
        h1 { text-align: center; color: #9c27b0; font-weight: 700; margin-bottom: 1em; }
        h2 { color: #9c27b0; font-weight: 700; border-bottom: 1px solid #e0e0e0; padding-bottom: 0.5em; }
        .question { page-break-inside: avoid; }
        .answer-choice { page-break-inside: avoid; }
      </style>
      ${content}
    `;
    element.style.padding = '20px';
    element.className = 'pdf-container';

    const opt = {
      margin: 0.7,
      filename: `${problemSet.title.replace(/[^a-z0-9]/gi, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        windowWidth: 1200,
        scrollY: -window.scrollY,
        allowTaint: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait',
        compress: true,
        hotfixes: ['px_scaling'],
        precision: 16
      },
      pagebreak: { 
        mode: ['css', 'legacy', 'avoid-all'],
        before: ['.answer-key-section'],
        avoid: ['.question', '.answer-choices', '.answer-choice', 'h1', 'h2', 'h3', 'p']
      }
    } as any;

    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      await (html2pdf() as any).set(opt).from(element).save();
      handleDownloadClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadWord = () => {
    if (!problemSet) return;
    const content = createPrintContent(problemSet, 'word');
    const formattedContent = `
      <html xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns:v="urn:schemas-microsoft-com:vml"
            xmlns:o="urn:schemas-microsoft-com:office:office">
        <head>
          <meta charset="utf-8">
          <title>${problemSet.title}</title>
          <style>
            body { font-family: 'Roboto', Arial, sans-serif; font-size: 12pt; line-height: 1.6; }
            h1 { text-align: center; color: #9c27b0; font-weight: 700; }
            h2 { color: #9c27b0; font-weight: 700; }
            .question { margin-bottom: 2em; }
            .answer-choice { margin-bottom: 0.5em; }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;

    const blob = new Blob([formattedContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${problemSet.title.replace(/[^a-z0-9]/gi, '_')}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    handleDownloadClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Typography variant="h6" component="div">
            {problemSet.title}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Grade Level: {problemSet.gradeLevel} • Questions: {problemSet.questions.length}
            {problemSet.strand && ` • Strand: ${problemSet.strand}`}
            {problemSet.standards && problemSet.standards.length > 0 && (
              <>
                {' • '}
                Standards: {problemSet.standards.map(std => std.code).join(', ')}
              </>
            )}
          </Typography>
        </Box>

        {/* Questions */}
        <Box sx={{ mb: 4 }}>
          {problemSet.questions.map((question, index) => (
            <QuestionDisplay 
              key={index} 
              question={question} 
              index={index} 
              showAnswerKey={true} 
            />
          ))}
        </Box>

        {/* Answer Key */}
        {problemSet.answerKey && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Complete Answer Key
            </Typography>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Box sx={{
                '& p': { lineHeight: 1.8, mb: 1 },
                '& .katex': { fontSize: '1.1em' }
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                  {problemSet.answerKey}
                </ReactMarkdown>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          aria-controls="view-download-menu"
          aria-haspopup="true"
          onClick={handleDownloadClick}
        >
          Download
        </Button>
        <Menu
          anchorEl={downloadAnchorEl}
          open={Boolean(downloadAnchorEl)}
          onClose={handleDownloadClose}
          id="view-download-menu"
        >
          <MenuItem onClick={handleDownloadPDF}>
            <PictureAsPdfIcon style={{ marginRight: 8 }} />
            Download as PDF
          </MenuItem>
          <MenuItem onClick={handleDownloadWord}>
            <DescriptionIcon style={{ marginRight: 8 }} />
            Download as Word
          </MenuItem>
        </Menu>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main MathWordProblemsList component
const MathWordProblemsList: React.FC = () => {
  const [problemSets, setProblemSets] = useState<MathWordProblemListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProblemSet, setSelectedProblemSet] = useState<MathWordProblem | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  useEffect(() => {
    fetchProblemSets();
  }, []);

  const fetchProblemSets = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching math word problem sets...');
      const response = await apiAxiosInstance.get('/api/math-word-problems');
      console.log('Response:', response.data);
      setProblemSets(response.data);
    } catch (err) {
      console.error('Error fetching math word problem sets:', err);
      setError('Failed to load math word problem sets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProblemSet = async (problemSet: MathWordProblemListItem) => {
    try {
      // Fetch full details for viewing
      const response = await apiAxiosInstance.get(`/api/math-word-problems/${problemSet._id}`);
      setSelectedProblemSet(response.data);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Error fetching problem set details:', err);
      setSnackbar({ open: true, message: 'Failed to load problem set details. Please try again.', severity: 'error' });
    }
  };

  const handleDeleteProblemSet = async (problemSetId: string) => {
    if (!window.confirm('Are you sure you want to delete this problem set? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteLoading(problemSetId);
      await apiAxiosInstance.delete(`/api/math-word-problems/${problemSetId}`);
      setSnackbar({ open: true, message: 'Problem set deleted successfully', severity: 'success' });
      
      // Remove the deleted problem set from the local state
      setProblemSets(prev => prev.filter(ps => ps._id !== problemSetId));
    } catch (err) {
      console.error('Error deleting problem set:', err);
      setSnackbar({ open: true, message: 'Failed to delete problem set. Please try again.', severity: 'error' });
    } finally {
      setDeleteLoading(null);
    }
  };

  const allSelected = useMemo(() => {
    return problemSets.length > 0 && selectedIds.length === problemSets.length;
  }, [selectedIds, problemSets]);

  const someSelected = useMemo(() => {
    return selectedIds.length > 0 && selectedIds.length < problemSets.length;
  }, [selectedIds, problemSets]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(problemSets.map(ps => ps._id));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const idsToDelete = [...selectedIds];
      const results = await Promise.allSettled(
        idsToDelete.map(id => apiAxiosInstance.delete(`/api/math-word-problems/${id}`))
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        setProblemSets(prev => prev.filter(ps => !idsToDelete.includes(ps._id)));
      }
      setSelectedIds([]);
      setSnackbar({
        open: true,
        message: failedCount === 0 ? `Deleted ${successCount} problem set(s)` : `Deleted ${successCount} problem set(s); ${failedCount} failed`,
        severity: failedCount === 0 ? 'success' : 'error'
      });
    } catch (err) {
      console.error('Bulk delete error:', err);
      setSnackbar({ open: true, message: 'Failed to delete selected problem sets.', severity: 'error' });
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
        Math Word Problems Library
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage your saved math word problem sets
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Bulk actions toolbar */}
      <Toolbar sx={{ pl: { sm: 0 }, pr: { xs: 1, sm: 1 }, justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">
          {selectedIds.length > 0 ? `${selectedIds.length} selected` : `${problemSets.length} item(s)`}
        </Typography>
        <Tooltip title={selectedIds.length === 0 ? 'Select rows to enable' : 'Delete selected'}>
          <span>
            <Button
              variant="contained"
              color="error"
              disabled={selectedIds.length === 0}
              onClick={openBulkDelete}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
          </span>
        </Tooltip>
      </Toolbar>

      {problemSets.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Problem Sets Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You haven't created any math word problem sets yet. Start by creating your first problem set using the Math Word Problems tool.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="math word problems table" size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ width: '45%' }}>Title</TableCell>
                <TableCell sx={{ width: 80, textAlign: 'center' }}>Grade</TableCell>
                <TableCell sx={{ width: 110, textAlign: 'center' }}>Questions</TableCell>
                <TableCell sx={{ width: 140 }}>Created</TableCell>
                <TableCell align="right" sx={{ width: 200, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {problemSets.map((problemSet) => {
                const isChecked = selectedIds.includes(problemSet._id);
                return (
                  <TableRow key={problemSet._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox checked={isChecked} onChange={() => toggleSelectOne(problemSet._id)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {problemSet.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{problemSet.gradeLevel}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {problemSet.numQuestions || 0}
                    </TableCell>
                    <TableCell>{formatDate(problemSet.createdAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewProblemSet(problemSet)}
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteProblemSet(problemSet._id)}
                          disabled={deleteLoading === problemSet._id}
                          sx={{ '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
                        >
                          {deleteLoading === problemSet._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Dialog */}
      <ViewProblemSetDialog
        open={viewDialogOpen}
        problemSet={selectedProblemSet}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedProblemSet(null);
        }}
      />

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete Selected Problem Sets</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedIds.length} selected problem set(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={isBulkDeleting}>Cancel</Button>
          <Button color="error" onClick={handleConfirmBulkDelete} disabled={isBulkDeleting} startIcon={isBulkDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}>
            {isBulkDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MathWordProblemsList;
